<script setup lang="ts">
import { community, projects } from '../../data/projects'

const currentYear = new Date().getFullYear()
</script>

<template>
  <footer class="site-footer">
    <div class="footer-aura" aria-hidden="true" />
    <div class="footer-inner">
      <div class="footer-lead">
        <div class="footer-brand">
          <span class="footer-mark" aria-hidden="true" />
          <div>
            <span class="footer-logo">The Calming Storm</span>
            <p class="footer-tagline">Self-hosted Minecraft community.<br />Free forever — no paid ranks, no compromises.</p>
          </div>
        </div>
        <a class="footer-discord" :href="community.discord" target="_blank" rel="noopener noreferrer">
          <span>
            <small>Build with us</small>
            Join the Discord
          </span>
          <span class="arrow" aria-hidden="true">↗</span>
        </a>
      </div>

      <div class="footer-grid">
        <div class="footer-col">
          <h2>Explore</h2>
          <RouterLink to="/">Home</RouterLink>
          <RouterLink to="/events">Events</RouterLink>
          <RouterLink to="/leaderboards">Leaderboards</RouterLink>
          <RouterLink to="/dashboard">Dashboard</RouterLink>
        </div>

        <div class="footer-col">
          <h2>Projects</h2>
          <a
            v-for="project in projects"
            :key="project.name"
            :href="project.url"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ project.name }}
          </a>
        </div>

        <div class="footer-col">
          <h2>Community</h2>
          <a :href="community.discord" target="_blank" rel="noopener noreferrer">Discord</a>
          <a :href="community.donate" target="_blank" rel="noopener noreferrer">Donate</a>
          <RouterLink to="/admin">Admin</RouterLink>
        </div>
      </div>

      <div class="footer-bottom">
        <p>&copy; {{ currentYear }} The Calming Storm</p>
        <p>Donations are appreciated but never required. All servers are maintained as a community project.</p>
      </div>
    </div>
  </footer>
</template>

<style scoped>
.site-footer {
  position: relative;
  z-index: 1;
  margin-top: clamp(4rem, 9vw, 8rem);
  overflow: hidden;
  border-top: 1px solid var(--border-subtle);
  background: rgba(7, 9, 17, 0.9);
}

.site-footer::before {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 1px;
  content: '';
  background: linear-gradient(90deg, transparent 8%, rgba(167, 139, 250, 0.5), rgba(103, 232, 249, 0.3), transparent 92%);
}

.footer-aura {
  position: absolute;
  top: -280px;
  left: 18%;
  width: 560px;
  height: 420px;
  border-radius: 50%;
  background: rgba(109, 40, 217, 0.14);
  filter: blur(100px);
  pointer-events: none;
}

.footer-inner {
  position: relative;
  width: min(100%, var(--site-width));
  margin-inline: auto;
  padding: clamp(3rem, 6vw, 5rem) var(--gutter) 1.5rem;
}

.footer-lead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  padding-bottom: 2.5rem;
  border-bottom: 1px solid var(--border-subtle);
}

.footer-brand {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.footer-mark {
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  border: 1px solid rgba(196, 181, 253, 0.4);
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(103, 232, 249, 0.08));
  box-shadow: inset 0 0 20px rgba(139, 92, 246, 0.2);
  transform: rotate(45deg);
}

.footer-logo {
  display: block;
  margin-bottom: 0.45rem;
  font-size: 1.15rem;
  font-weight: 760;
  letter-spacing: -0.03em;
}

.footer-tagline {
  color: var(--text-muted);
  font-size: 0.84rem;
  line-height: 1.65;
}

.footer-discord {
  min-width: 220px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  padding: 0.9rem 1rem;
  border: 1px solid rgba(167, 139, 250, 0.26);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  background: rgba(139, 92, 246, 0.09);
  font-size: 0.88rem;
  font-weight: 650;
}

.footer-discord small {
  display: block;
  margin-bottom: 0.15rem;
  color: var(--color-secondary);
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.footer-discord .arrow {
  color: var(--color-primary-soft);
  font-size: 1.25rem;
  transition: transform var(--transition-normal);
}

.footer-discord:hover {
  border-color: rgba(103, 232, 249, 0.4);
  color: #fff;
  background: rgba(139, 92, 246, 0.14);
}

.footer-discord:hover .arrow {
  transform: translate(2px, -2px);
}

.footer-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(130px, 1fr));
  gap: 2rem;
  max-width: 680px;
  padding: 2.5rem 0;
}

.footer-col h2 {
  margin-bottom: 0.7rem;
  color: var(--text-secondary);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.footer-col a {
  width: fit-content;
  display: block;
  padding: 0.25rem 0;
  color: var(--text-muted);
  font-size: 0.84rem;
}

.footer-col a:hover {
  color: var(--color-secondary);
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  gap: 1.5rem;
  padding-top: 1.35rem;
  border-top: 1px solid var(--border-subtle);
  color: var(--text-faint);
  font-size: 0.7rem;
}

@media (max-width: 700px) {
  .footer-lead {
    align-items: stretch;
    flex-direction: column;
  }

  .footer-discord {
    width: 100%;
  }

  .footer-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .footer-bottom {
    flex-direction: column;
    gap: 0.35rem;
  }
}

@media (max-width: 440px) {
  .footer-grid {
    grid-template-columns: 1fr;
  }
}
</style>
